/**
 * NOS-113 / NOS-178: URL input + ownership checkbox + Import button for site clone flow.
 * Progress is real (polled from the import_jobs row), not simulated.
 */
import { useState, useCallback } from "react"
import { Check, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import type { CrawlPageMeta, ImportJobProgress, SiteCloneStatus } from "@/hooks/useSiteClone"

function isValidUrl(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) return false
  try {
    const url = new URL(trimmed)
    return url.protocol === "http:" || url.protocol === "https:"
  } catch {
    return false
  }
}

/** Strip " | Site Name" suffix from page titles for compact display. */
function shortTitle(title: string): string {
  for (const sep of [" | ", " - ", " – ", " — "]) {
    const idx = title.indexOf(sep)
    if (idx > 0) return title.slice(0, idx).trim()
  }
  return title.length > 38 ? `${title.slice(0, 38)}…` : title
}

/** NOS-192: stage-1 preview target — homepage + up to 3 nav pages + up to 2 posts. */
const PREVIEW_SAMPLE_TARGET = 6

/**
 * Overall progress: crawl phase fills 0–45%, mapping fills 45–98%.
 * NOS-192: during the preview stage the denominator is the sample target, not
 * the full discovered inventory — otherwise the bar would sit near zero.
 */
export function importProgressPct(
  status: SiteCloneStatus,
  progress: ImportJobProgress | null
): number {
  if (!progress || progress.pagesFound === 0) return status === "mapping" ? 45 : 2
  const found =
    progress.stage === "preview"
      ? Math.min(progress.pagesFound, PREVIEW_SAMPLE_TARGET)
      : progress.pagesFound
  const ratio = Math.min(1, progress.pagesProcessed / Math.max(found, 1))
  if (status === "crawling") return Math.max(2, Math.round(ratio * 45))
  if (status === "mapping") return 45 + Math.round(ratio * 53)
  return 100
}

export interface SiteCloneEntryProps {
  orgId: string
  onImportComplete: () => void
  onBack: () => void
  onStartFresh?: () => void
  initiateCrawl: (
    url: string,
    orgId: string,
    opts?: { pageCap?: number; importDesign?: boolean }
  ) => Promise<{ ok: boolean; error?: string }>
  /** Watchdog in ms; defaults to 15 minutes — large sites crawl + map as an async job */
  timeoutMs?: number
  cloneStatus?: SiteCloneStatus
  crawlPages?: CrawlPageMeta[]
  /** Live counts from the import_jobs row (NOS-178) */
  jobProgress?: ImportJobProgress | null
  /** NOS-184: shows the page-cap override field (server re-verifies admin) */
  isPlatformAdmin?: boolean
}

/** NOS-184: bounds for the admin page-cap field (server clamps independently). */
const ADMIN_CAP_MIN = 1
const ADMIN_CAP_MAX = 200
const DEFAULT_CAP = 50

type CloneState = "form" | "loading" | "error"

export function SiteCloneEntry({
  orgId,
  onImportComplete,
  onBack,
  onStartFresh,
  initiateCrawl,
  timeoutMs = 900_000,
  cloneStatus,
  crawlPages = [],
  jobProgress = null,
  isPlatformAdmin = false,
}: SiteCloneEntryProps) {
  const [url, setUrl] = useState("")
  const [ownershipConfirmed, setOwnershipConfirmed] = useState(false)
  const [state, setState] = useState<CloneState>("form")
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [pageCapInput, setPageCapInput] = useState(String(DEFAULT_CAP))
  // NOS-189: import scope — content + design (default) vs content only
  const [importDesign, setImportDesign] = useState(true)

  const urlValid = isValidUrl(url)
  const canSubmit = urlValid && ownershipConfirmed

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || state !== "form") return
    const trimmedUrl = url.trim()
    setState("loading")
    setErrorMessage(null)

    let timedOut = false
    const timeoutId = setTimeout(() => {
      timedOut = true
      setState("error")
      setErrorMessage("Import timed out. Please try again.")
    }, timeoutMs)

    try {
      // Only send a page cap when the admin actually changed it off the default
      const parsedCap = Math.trunc(Number(pageCapInput))
      const pageCap =
        isPlatformAdmin && Number.isFinite(parsedCap) && parsedCap !== DEFAULT_CAP
          ? Math.min(Math.max(parsedCap, ADMIN_CAP_MIN), ADMIN_CAP_MAX)
          : undefined
      // NOS-189: only send import_design when opting out of design carry-over
      const opts: { pageCap?: number; importDesign?: boolean } = {}
      if (pageCap != null) opts.pageCap = pageCap
      if (!importDesign) opts.importDesign = false
      const result = await initiateCrawl(
        trimmedUrl,
        orgId,
        Object.keys(opts).length > 0 ? opts : undefined
      )
      clearTimeout(timeoutId)
      if (timedOut) return
      if (result.ok) {
        onImportComplete()
      } else {
        setState("error")
        setErrorMessage(result.error ?? "Could not import this site. Please try again.")
      }
    } catch (err) {
      clearTimeout(timeoutId)
      if (timedOut) return
      setState("error")
      setErrorMessage(
        err instanceof Error ? err.message : "Could not import this site. Please try again."
      )
    }
  }, [canSubmit, state, url, orgId, initiateCrawl, onImportComplete, timeoutMs, isPlatformAdmin, pageCapInput, importDesign])

  if (state === "loading") {
    const total = crawlPages.length
    const isMappingPhase = cloneStatus === "mapping"
    const progressPct = importProgressPct(cloneStatus ?? "crawling", jobProgress)
    const found = jobProgress?.pagesFound ?? 0
    const processed = jobProgress?.pagesProcessed ?? 0
    const overflow = jobProgress?.pagesOverflow ?? 0
    // NOS-192: stage 1 imports a small preview first; the user approves the rest
    const isPreviewStage = jobProgress?.stage === "preview"
    // During mapping, the server-side counter drives the per-page checklist
    const currentMappingIndex = isMappingPhase ? Math.min(processed, Math.max(total - 1, 0)) : 0

    return (
      <Card>
        <CardHeader className="space-y-1 text-center pb-3">
          <CardTitle className="text-xl">
            {isPreviewStage ? "Importing a preview of your site" : "Importing your site"}
          </CardTitle>
          <CardDescription>
            {isPreviewStage
              ? "We import a small sample first so you can check it before importing everything."
              : isMappingPhase
              ? "Our AI is analyzing and importing your content…"
              : "Scanning your website for pages and content…"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Status log — AI-chat style */}
          <div className="rounded-md border bg-muted/30 px-4 py-3 space-y-2 text-sm font-mono">
            {/* Crawl step */}
            <div className="flex items-center gap-2.5">
              {!isMappingPhase ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-primary flex-shrink-0" />
              ) : (
                <Check className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
              )}
              <span className={isMappingPhase ? "text-foreground" : "text-muted-foreground"}>
                {isMappingPhase
                  ? `Crawled ${total} page${total !== 1 ? "s" : ""}`
                  : found > 0
                  ? `Crawling your website… ${processed} of ${found} pages`
                  : "Crawling your website…"}
              </span>
            </div>

            {/* Per-page mapping rows */}
            {isMappingPhase && total > 0 && (
              <div className="space-y-1.5 pl-6 pt-0.5">
                {crawlPages.map((page, i) => {
                  const isDone = i < currentMappingIndex
                  const isCurrent = i === currentMappingIndex
                  return (
                    <div key={page.url} className="flex items-center gap-2">
                      {isDone ? (
                        <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                      ) : isCurrent ? (
                        <Loader2 className="h-3 w-3 animate-spin text-primary flex-shrink-0" />
                      ) : (
                        <div className="h-3 w-3 flex-shrink-0 rounded-full border border-muted-foreground/25" />
                      )}
                      <span
                        className={
                          isDone || isCurrent
                            ? "text-foreground"
                            : "text-muted-foreground/50"
                        }
                      >
                        {shortTitle(page.title)}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Mapping step with no pages yet */}
            {isMappingPhase && total === 0 && (
              <div className="flex items-center gap-2.5 pl-0.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-primary flex-shrink-0" />
                <span className="text-muted-foreground">Mapping content with AI…</span>
              </div>
            )}
          </div>

          {/* NOS-178: overflow past the per-phase page cap is surfaced, never silent */}
          {overflow > 0 && (
            <p className="text-xs text-muted-foreground">
              Your site has {overflow} more page{overflow !== 1 ? "s" : ""} than the import
              limit of 50 — the most important pages were selected.
            </p>
          )}

          {/* Progress bar */}
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>
                {!isMappingPhase
                  ? found > 0
                    ? `Crawling ${processed} of ${found} pages…`
                    : "Crawling…"
                  : total > 0
                  ? `Page ${Math.min(currentMappingIndex + 1, total)} of ${total}`
                  : "Mapping…"}
              </span>
              <span>{progressPct}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all duration-1000 ease-out"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (state === "error") {
    return (
      <Card>
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-xl">Import failed</CardTitle>
          <CardDescription>{errorMessage}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button onClick={() => { setState("form"); setErrorMessage(null) }}>
              Try again
            </Button>
            {onStartFresh && (
              <Button variant="outline" onClick={onStartFresh}>
                Start Fresh instead
              </Button>
            )}
          </div>
        </CardContent>
        <CardFooter>
          <Button type="button" variant="ghost" onClick={onBack}>
            Go back
          </Button>
        </CardFooter>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="space-y-1 text-center">
        <CardTitle className="text-xl">Import your existing website</CardTitle>
        <CardDescription>
          Enter your current website URL and we&apos;ll import your content.
        </CardDescription>
      </CardHeader>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
      >
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="clone-url">Your current website URL</Label>
            <Input
              id="clone-url"
              type="url"
              placeholder="https://www.example.org"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className={url.length > 0 && !urlValid ? "border-destructive" : ""}
            />
            {url.length > 0 && !urlValid && (
              <p className="text-sm text-destructive">URL must start with http:// or https://</p>
            )}
          </div>
          <div className="flex items-start space-x-2">
            <Checkbox
              id="ownership-confirm"
              checked={ownershipConfirmed}
              onCheckedChange={(checked) => setOwnershipConfirmed(checked === true)}
            />
            <Label
              htmlFor="ownership-confirm"
              className="cursor-pointer text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              I confirm this is my organization&apos;s website
            </Label>
          </div>
          {/* NOS-189: import scope — design carry-over vs fresh NOS design */}
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">What should we import?</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              <button
                type="button"
                role="radio"
                aria-checked={importDesign}
                onClick={() => setImportDesign(true)}
                className={`rounded-md border p-3 text-left transition-colors ${
                  importDesign
                    ? "border-primary ring-1 ring-primary bg-primary/5"
                    : "border-input hover:border-muted-foreground/40"
                }`}
              >
                <span className="block text-sm font-medium">Content and design</span>
                <span className="block text-xs text-muted-foreground">
                  Keep your current look — bring over layout and styles.
                </span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={!importDesign}
                onClick={() => setImportDesign(false)}
                className={`rounded-md border p-3 text-left transition-colors ${
                  !importDesign
                    ? "border-primary ring-1 ring-primary bg-primary/5"
                    : "border-input hover:border-muted-foreground/40"
                }`}
              >
                <span className="block text-sm font-medium">Content only</span>
                <span className="block text-xs text-muted-foreground">
                  Bring your content into a fresh NOS design.
                </span>
              </button>
            </div>
          </fieldset>
          {isPlatformAdmin && (
            <div className="space-y-2 rounded-md border border-dashed p-3">
              <Label htmlFor="admin-page-cap">Max pages to import (admin)</Label>
              <Input
                id="admin-page-cap"
                type="number"
                min={ADMIN_CAP_MIN}
                max={ADMIN_CAP_MAX}
                value={pageCapInput}
                onChange={(e) => setPageCapInput(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Platform-admin override for white-glove imports (default {DEFAULT_CAP}, max{" "}
                {ADMIN_CAP_MAX}). Large imports run unattended — ~200 pages takes about an hour.
              </p>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex gap-2">
          <Button type="button" variant="outline" onClick={onBack}>
            Go back
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            Import Site
          </Button>
        </CardFooter>
      </form>
    </Card>
  )
}
